import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Journey,
  PageAbsolutePath,
  PageRelativePath
} from '../../../consts/Common';
import { VehicleState, VehicleType } from '../../common/CommonTypes';
import VehiclePage from '../../common/VehiclePage/VehiclePage';
import { SiteContext } from '../../../context/SiteContext';
import { VehicleApi } from '../../../api/VehicleApi';
import PageHeader from '../../common/PageHeader';
import { ButtonBack } from '../../common/Button/ButtonBack';
import { ButtonContinue } from '../../common/Button/ButtonContinue';
import { useJourney } from '../Layout';
import { CommonValidation } from '../../common/CommonValidation/commonValidation';
import LoadingContainer from '../../common/LoadingContainer/LoadingContainer';
import { useTranslation } from 'react-i18next';
import { setCountryCode } from '../../common/HelperMethods';
import { blankVehicle } from '../../testData/journeyHelper';

const RenewalsVehicleDetails = (): JSX.Element => {
  const {
    onPageChange,
    onUpdateVehicleDetails,
    journey,
    onPolicyStartDateChange
  } = useJourney();
  const [loadingVehicle, setLoadingVehicle] = useState(false);
  const { vehicle, policyStartDate } = journey;
  const navigate = useNavigate();
  const [updateVehicleDetails, setUpdateVehicleDetails] = useState<
    boolean | null
  >(null);
  const [validateVehicleDetails, setValidateVehicleDetails] = useState<
    boolean | null
  >(null);
  const [goForward, setGoForward] = useState(false);
  const [goBack, setGoBack] = useState(false);
  const [registrationNumber, setRegistrationNumber] = useState('');
  const [vin, setVin] = useState('');

  const { t } = useTranslation();

  const loadingVehicleMessage = t('vehicle-details.loading-vehicle-message');
  const purchasePriceLabel = t('vehicle-details.purchase-price-label');
  const backButtonText = t('vehicle-details.back-button-text');
  const buttonGetQuotesText = t('vehicle-details.button-get-quotes-text');
  const callCentreVehicleDetailsTitle = t(
    'vehicle-details.call-centre-vehicle-details-title'
  );

  const { site } = useContext(SiteContext);
  const cms = site.content.vehiclePage;

  const onBack = () => {
    setUpdateVehicleDetails(true);
    setGoBack(true);
  };

  const onNext = () => {
    setValidateVehicleDetails(true);
    setGoForward(true);
  };

  const initialVehicleDetails: VehicleType = blankVehicle;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (updateVehicleDetails === false && goForward === true) {
      navigate(PageRelativePath.Quote);
      onPageChange(PageAbsolutePath.Renewals.VehicleDetails, true);
    }

    if (updateVehicleDetails === false && goBack === true) {
      if (site.journeyType === Journey.CallCentre) {
        const path = setCountryCode(
          PageAbsolutePath.Renewals.Landing,
          site.countryCode
        );
        navigate(path);
      } else {
        navigate(PageRelativePath.PersonalDetails);
        onPageChange(PageAbsolutePath.Renewals.PersonalDetails, false);
      }
    }
  }, [updateVehicleDetails]);

  useEffect(() => {
    if (CommonValidation.isNotEmpty(registrationNumber)) {
      setLoadingVehicle(true);

      const controller = new AbortController();

      fetchVehicle(registrationNumber, controller.signal);

      return () => {
        controller.abort();
      };
    }
  }, [registrationNumber]);

  useEffect(() => {
    if (CommonValidation.isNotEmpty(vin)) {
      const vehicleDetails: VehicleType = {
        ...vehicle.vehicleDetails,
        vin: vin
      };

      onUpdateVehicleDetails({
        ...vehicle,
        vehicleDetails: vehicleDetails
      });
    }
  }, [vin]);

  const onUpdateVehicleState = (vs: VehicleState) => {
    onUpdateVehicleDetails(vs);
    setUpdateVehicleDetails(false);
  };

  const onVehicleValidated = (isValid: boolean) => {
    if (isValid === true) {
      setValidateVehicleDetails(false);
      setUpdateVehicleDetails(true);
    } else {
      setValidateVehicleDetails(false);
      setGoForward(false);
    }
  };

  const vehicleNotFound =
    loadingVehicle ||
    vehicle.vehicleDetails.registrationDate === '' ||
    vehicle.vehicleDetails.registrationDate === null;

  async function fetchVehicle(reg: string, signal: AbortSignal) {
    const fetchVehicleSuccess = (userVehicle: VehicleType) => {
      if (
        userVehicle.registrationDate === '' ||
        userVehicle.registrationDate === null
      ) {
        userVehicle.errorOrigin = 'vrn';
        userVehicle.errorMessage = t(
          'common-error-msg.api-error-msg.vehicle-vreg-error'
        );
      }

      onUpdateVehicleDetails({
        ...vehicle,
        vehicleDetails: userVehicle
      });

      setLoadingVehicle(false);
    };

    const fetchVehicleFailure = () => {
      onUpdateVehicleDetails({
        ...vehicle,
        vehicleDetails: initialVehicleDetails
      });

      setLoadingVehicle(false);
    };

    await new VehicleApi().getRegisteredVehicles(
      reg,
      site.user.token,
      fetchVehicleSuccess,
      fetchVehicleFailure,
      signal
    );
  }

  return (
    <div>
      {site.journeyType === Journey.CallCentre ? (
        <h3>{callCentreVehicleDetailsTitle}</h3>
      ) : (
        <PageHeader
          title={cms.title}
          description={cms.description}
          information={cms.information}
          warning={cms.warning}
          descriptionClass='p2'
        />
      )}

      <LoadingContainer
        loading={loadingVehicle}
        message={loadingVehicleMessage}>
        <VehiclePage
          initialVehicleInfo={vehicle}
          policyStartDate={policyStartDate}
          update={updateVehicleDetails || false}
          validate={validateVehicleDetails || false}
          showEditVrn={site.journeyType === Journey.CallCentre}
          showGapIntroductionDate={false}
          showModelTrim={site.journeyType === Journey.CallCentre}
          showPolicyStartDate={true}
          showSalesId={false}
          showVehicleDeliveryDate={false}
          showDealerReference={false}
          showFieldsOnDataError={
            (site.journeyType === Journey.CallCentre && vehicleNotFound) ||
            !vehicleNotFound
          }
          vehiclePurchasePriceLabel={purchasePriceLabel}
          token={site.user.token}
          isPolicyStartDateEnabled={false}
          showVin={!cms.hideVinInput}
          showIsDeliveryDateEstimated={false}
          onValidated={onVehicleValidated}
          onUpdateVehicleState={onUpdateVehicleState}
          onUpdateRegistrationNumber={setRegistrationNumber}
          onUpdateIdentificationNumber={setVin}
          onUpdatePolicyStartDate={onPolicyStartDateChange}
          isReadOnly={false}
          futureDateAllowed={false}
          showNeedHelpTooltipDeliveryDate={true}
          showVehiclePurchasePrice={true}
          showEditVehicle={site.journeyType === Journey.CallCentre}
          showMileage={true}
          isPurchasePriceEditable={true}
          isMileageEditable={true}
          isSalesIdMandatory={false}
          culture={site.cultureCode}
          isVehicleDeliveryEditable={true}
          externalReferenceLabelText=''
        />

        <div className='flex-row form-nav'>
          {site.journeyType !== Journey.CallCentre && (
            <ButtonBack name='btnBack' onClick={onBack} text={backButtonText} />
          )}
          <ButtonContinue
            name='btn-see-products'
            onClick={onNext}
            text={buttonGetQuotesText}
            disabled={vehicleNotFound}
          />
        </div>
      </LoadingContainer>
    </div>
  );
};

export default RenewalsVehicleDetails;
